import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { Locale } from '@castfeeds/i18n';
import type { ThemePref } from '@/lib/api/types';

/**
 * UI language and theme. Kept locally (also before login, read by public/theme-boot.js to avoid a flash)
 * and synced to the user's profile with PATCH /v1/auth/me when signed in.
 */
export interface PrefsState {
  locale: Locale;
  theme: ThemePref;
  setLocale: (l: Locale) => void;
  setTheme: (t: ThemePref) => void;
}

const browserLocale = (): Locale => (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('he') ? 'he' : 'en');

export const usePrefs = create<PrefsState>()(
  persist(
    (set) => ({
      locale: browserLocale(),
      theme: 'system',
      setLocale: (locale) => set({ locale }),
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'cf.prefs', storage: createJSONStorage(() => localStorage), version: 1 },
  ),
);

export const THEME_CYCLE: ThemePref[] = ['dark', 'light', 'system'];
export function nextTheme(t: ThemePref): ThemePref {
  return THEME_CYCLE[(THEME_CYCLE.indexOf(t) + 1) % THEME_CYCLE.length];
}

export function resolveTheme(t: ThemePref, prefersLight: boolean): 'light' | 'dark' {
  return t === 'system' ? (prefersLight ? 'light' : 'dark') : t;
}
